import { reaction, IReactionDisposer, runInAction } from "mobx";

export interface BatchedReactionOptions {
  delay?: number;
  maxWait?: number;
  leading?: boolean;
  trailing?: boolean;
  fireImmediately?: boolean;
}

export interface ReactionBatch {
  id: string;
  reactions: Array<{
    id: string;
    expression: () => unknown;
    effect: (arg: unknown, prev: unknown) => void;
    options?: BatchedReactionOptions;
  }>;
  disposer?: IReactionDisposer;
  isActive: boolean;
}

/**
 * ReactionManager - Optimizes MobX reactions through batching and debouncing
 * Reduces performance overhead from 340+ individual reactions
 */
export class ReactionManager {
  private batches: Map<string, ReactionBatch> = new Map();
  private pendingUpdates: Map<string, unknown> = new Map();
  private updateTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private debugMode: boolean = false;

  constructor(debugMode = false) {
    this.debugMode = debugMode;
    this.updateGlobalReactionCount();
  }

  /**
   * Create a batched reaction that groups multiple related reactions together
   */
  createBatchedReaction<T>(
    batchId: string,
    expression: () => T,
    effect: (arg: T, prev: T) => void,
    options: BatchedReactionOptions = {}
  ): IReactionDisposer {
    const {
      delay = 100,
      maxWait = 1000,
      leading = false,
      trailing = true,
      fireImmediately = false
    } = options;

    let timeoutId: NodeJS.Timeout | null = null;
    let maxTimeoutId: NodeJS.Timeout | null = null;
    let lastCallTime = 0;
    let lastArgs: T;
    let prevArgs: T;

    const debouncedEffect = (arg: T, prev: T) => {
      const now = Date.now();
      lastArgs = arg;
      prevArgs = prev;

      const shouldCallLeading = leading && !timeoutId;
      const shouldCallTrailing = trailing;

      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Set max wait timeout if not already set
      if (!maxTimeoutId && maxWait > 0) {
        maxTimeoutId = setTimeout(() => {
          this.executeEffect(batchId, effect, lastArgs, prevArgs);
          maxTimeoutId = null;
          lastCallTime = Date.now();
        }, maxWait);
      }

      // Execute immediately if leading edge
      if (shouldCallLeading) {
        this.executeEffect(batchId, effect, arg, prev);
        lastCallTime = now;
        return;
      }

      // Set trailing timeout
      if (shouldCallTrailing) {
        timeoutId = setTimeout(() => {
          this.executeEffect(batchId, effect, lastArgs, prevArgs);
          if (maxTimeoutId) {
            clearTimeout(maxTimeoutId);
            maxTimeoutId = null;
          }
          timeoutId = null;
          lastCallTime = Date.now();
        }, delay);
      }
    };

    return reaction(expression, debouncedEffect, { fireImmediately });
  }

  /**
   * Create multiple related reactions that share the same debounce window
   */
  createReactionBatch(batchId: string, options: BatchedReactionOptions = {}): void {
    if (this.batches.has(batchId)) {
      console.warn(`ReactionManager: Batch ${batchId} already exists`);
      return;
    }

    const batch: ReactionBatch = {
      id: batchId,
      reactions: [],
      isActive: false
    };

    this.batches.set(batchId, batch);
    this.updateGlobalReactionCount();
    this.debugMode && console.log(`ReactionManager: Created batch ${batchId}`);
  }

  /**
   * Add a reaction to an existing batch
   */
  addToBatch<T>(
    batchId: string,
    reactionId: string,
    expression: () => T,
    effect: (arg: T, prev: T) => void,
    options: BatchedReactionOptions = {}
  ): string {
    const batch = this.batches.get(batchId);
    if (!batch) {
      throw new Error(`ReactionManager: Batch ${batchId} does not exist`);
    }

    batch.reactions.push({
      id: reactionId,
      expression,
      effect,
      options
    });

    this.debugMode && console.log(`ReactionManager: Added reaction ${reactionId} to batch ${batchId}`);
    return `${batchId}.${reactionId}`;
  }

  /**
   * Activate a batch - starts all reactions in the batch with shared debouncing
   */
  activateBatch(batchId: string): void {
    const batch = this.batches.get(batchId);
    if (!batch || batch.isActive) return;

    const { delay = 100 } = batch.reactions[0]?.options || {};
    
    // Create a combined reaction that monitors all expressions in the batch
    const combinedExpression = () => {
      return batch.reactions.map(r => ({
        id: r.id,
        value: r.expression()
      }));
    };

    type ReactionValue = { id: string; value: unknown };
    const combinedEffect = (current: ReactionValue[], previous: ReactionValue[]) => {
      const changes = current.filter((curr, index) => {
        const prev = previous?.[index];
        return prev === undefined || curr.value !== prev?.value;
      });

      if (changes.length === 0) return;

      // Batch execute all affected reactions
      runInAction(() => {
        changes.forEach((change) => {
          const reactionDef = batch.reactions.find(r => r.id === change.id);
          if (reactionDef) {
            const prevValue = previous?.find((p) => p.id === change.id)?.value;
            reactionDef.effect(change.value, prevValue);
          }
        });
      });

      this.debugMode && console.log(`ReactionManager: Executed batch ${batchId} with ${changes.length} changes`);
    };

    batch.disposer = this.createBatchedReaction(
      batchId,
      combinedExpression,
      combinedEffect,
      { delay, fireImmediately: true }
    );

    batch.isActive = true;
    this.debugMode && console.log(`ReactionManager: Activated batch ${batchId} with ${batch.reactions.length} reactions`);
  }

  /**
   * Dispose a specific batch
   */
  disposeBatch(batchId: string): void {
    const batch = this.batches.get(batchId);
    if (!batch) return;

    if (batch.disposer) {
      batch.disposer();
    }

    // Clear any pending timeouts for this batch
    const timeout = this.updateTimeouts.get(batchId);
    if (timeout) {
      clearTimeout(timeout);
      this.updateTimeouts.delete(batchId);
    }

    this.pendingUpdates.delete(batchId);
    this.batches.delete(batchId);
    this.updateGlobalReactionCount();

    this.debugMode && console.log(`ReactionManager: Disposed batch ${batchId}`);
  }

  /**
   * Dispose all batches
   */
  disposeAll(): void {
    this.debugMode && console.log(`ReactionManager: Disposing ${this.batches.size} batches`);
    
    for (const batchId of this.batches.keys()) {
      this.disposeBatch(batchId);
    }

    // Clear all timeouts
    for (const timeout of this.updateTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.updateTimeouts.clear();
    this.pendingUpdates.clear();
  }

  /**
   * Get statistics about current batches
   */
  getStats() {
    const totalReactions = Array.from(this.batches.values())
      .reduce((sum, batch) => sum + batch.reactions.length, 0);

    return {
      totalBatches: this.batches.size,
      totalReactions,
      activeBatches: Array.from(this.batches.values()).filter(b => b.isActive).length,
      pendingUpdates: this.pendingUpdates.size,
      batchDetails: Array.from(this.batches.values()).map(batch => ({
        id: batch.id,
        reactionCount: batch.reactions.length,
        isActive: batch.isActive
      }))
    };
  }

  private executeEffect<T>(
    batchId: string,
    effect: (arg: T, prev: T) => void,
    arg: T,
    prev: T
  ): void {
    try {
      runInAction(() => {
        effect(arg, prev);
      });
    } catch (error) {
      console.error(`ReactionManager: Error in batch ${batchId}:`, error);
    }
  }

  enableDebugMode(): void {
    this.debugMode = true;
  }

  disableDebugMode(): void {
    this.debugMode = false;
  }

  /**
   * Update global reaction count for memory monitoring
   */
  private updateGlobalReactionCount(): void {
    if (typeof window !== 'undefined') {
      const stats = this.getStats();
      (window as any).__REACTION_COUNT__ = stats.totalReactions;
    }
  }
}